import React, { Fragment, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";

import AdministrationHeader from "../../components/header-components/AdministrationHeader.jsx";
import TableCard from "../../components/ui-components/TableCard";
import FlexTable from "../../components/ui-components/FlexTable";
import EmptyState from "../../components/ui-components/EmptyState";
import Modal from "../../components/ui-components/Modal";
import StatusPill from "../Budgets/components/StatusPill";
import { toast } from "../../components/ui-components/Toast";
import { useHasCapability } from "../../authz/useCapabilities";
import { CAPS } from "../../authz/capabilities";
import { useRoles } from "../roles/api";
import {
  useServiceAccounts,
  createServiceAccount,
  rotateToken,
  revokeToken,
  deleteServiceAccount,
} from "./api";

// fmtDate renders an ISO timestamp as a short local date-time. Returns "—" for empty input.
const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

// Maps the backend's forbidden-capability guard (409
// SERVICE_ACCOUNT_FORBIDDEN_CAPABILITY, see backend_go
// cmd/api/service_account_handlers.go) and other known error codes to a
// message a tenant admin can act on, instead of a raw server error string.
function friendlyServiceAccountError(err, action = "save the service account") {
  const code = err?.serverBody?.error?.code;
  const serverMessage = err?.serverBody?.error?.message;
  if (code === "SERVICE_ACCOUNT_FORBIDDEN_CAPABILITY") {
    return `That role includes an admin capability a service account may not hold${
      serverMessage ? ` (${serverMessage})` : ""
    }. Choose a more restricted set of roles.`;
  }
  return serverMessage || err?.message || `Failed to ${action}. Please try again.`;
}

// CopyField renders a read-only value with a Copy button. Used for the
// one-time secret in the reveal modal.
const CopyField = ({ value }) => {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Couldn't copy — clipboard access blocked");
    }
  };
  return (
    <div className="flex items-center gap-2">
      <code className="flex-1 bg-surface-2 border border-card-bd rounded-ctrl px-3 py-2 text-sm font-mono break-all">
        {value}
      </code>
      <button
        type="button"
        onClick={copy}
        className="px-3 py-2 text-sm rounded-ctrl bg-primary text-white hover:bg-primary-hover transition-colors shrink-0 border-0 box-border"
      >
        Copy
      </button>
    </div>
  );
};

const emptyDraft = () => ({ label: "", roleCodes: [] });

// ServiceAccountsPage — the Settings-shell admin page for Integration Service
// Accounts (Sub-project D1): non-interactive machine-to-machine principals
// that authenticate with a "svc_..." bearer token and carry a restricted
// capability set via role assignment. Lists existing accounts, lets a
// security_admin/tenant_owner create one (label + roles), rotate/revoke its
// token, and deactivate it. The list itself always renders; create/rotate/
// revoke/deactivate are gated on service_accounts.manage — the same
// capability the backend routes require.
export default function ServiceAccountsPage() {
  const canManage = useHasCapability(CAPS.SERVICE_ACCOUNTS_MANAGE);
  const queryClient = useQueryClient();
  const accountsQ = useServiceAccounts();
  const rolesQ = useRoles();

  const accounts = accountsQ.data || [];
  const roles = useMemo(() => rolesQ.data || [], [rolesQ.data]);

  const roleNameByCode = useMemo(() => {
    const m = new Map();
    for (const r of roles) m.set(r.code, r.name);
    return m;
  }, [roles]);

  const invalidateAccounts = () => queryClient.invalidateQueries(["service-accounts"]);

  const [showCreate, setShowCreate] = useState(false);
  const [draft, setDraft] = useState(emptyDraft());
  const [revealedToken, setRevealedToken] = useState(null); // the one-time secret; never persisted
  const [rotateCandidate, setRotateCandidate] = useState(null);
  const [revokeCandidate, setRevokeCandidate] = useState(null);
  const [deactivateCandidate, setDeactivateCandidate] = useState(null);

  const createMutation = useMutation(
    () => createServiceAccount({ label: draft.label.trim(), roleCodes: draft.roleCodes }),
    {
      onSuccess: (res) => {
        setShowCreate(false);
        setDraft(emptyDraft());
        setRevealedToken(res?.token || null);
        invalidateAccounts();
      },
      onError: (err) => toast.error(friendlyServiceAccountError(err, "create the service account")),
    }
  );

  const rotateMutation = useMutation((id) => rotateToken(id), {
    onSuccess: (res) => {
      setRotateCandidate(null);
      setRevealedToken(res?.token || null);
      invalidateAccounts();
    },
    onError: (err) => {
      setRotateCandidate(null);
      toast.error(friendlyServiceAccountError(err, "rotate the token"));
    },
  });

  const revokeMutation = useMutation((id) => revokeToken(id), {
    onSuccess: () => {
      toast.success("Token revoked");
      setRevokeCandidate(null);
      invalidateAccounts();
    },
    onError: (err) => {
      setRevokeCandidate(null);
      toast.error(friendlyServiceAccountError(err, "revoke the token"));
    },
  });

  const deactivateMutation = useMutation((id) => deleteServiceAccount(id), {
    onSuccess: () => {
      toast.success("Service account deactivated");
      setDeactivateCandidate(null);
      invalidateAccounts();
    },
    onError: (err) => {
      setDeactivateCandidate(null);
      toast.error(friendlyServiceAccountError(err, "deactivate the service account"));
    },
  });

  const openCreate = () => {
    setDraft(emptyDraft());
    setShowCreate(true);
  };
  const closeCreate = () => setShowCreate(false);
  const closeReveal = () => setRevealedToken(null);

  const toggleRole = (code) => {
    setDraft((d) => ({
      ...d,
      roleCodes: d.roleCodes.includes(code)
        ? d.roleCodes.filter((c) => c !== code)
        : [...d.roleCodes, code],
    }));
  };

  const isDraftComplete = draft.label.trim().length > 0 && draft.roleCodes.length > 0;

  const handleCreate = () => {
    if (!isDraftComplete) {
      toast.error("Enter a label and select at least one role.");
      return;
    }
    createMutation.mutate();
  };

  const columns = useMemo(
    () => [
      {
        accessor: "label",
        Header: () => <span>Label</span>,
        Cell: ({ value }) => <span className="text-text font-medium truncate">{value}</span>,
        flex: 1,
        minWidth: 160,
      },
      {
        accessor: "roleCodes",
        Header: () => <span>Roles</span>,
        Cell: ({ value }) => (
          <div className="flex flex-wrap gap-1.5">
            {(value || []).length > 0 ? (
              (value || []).map((code) => (
                <span
                  key={code}
                  className="bg-surface-2 text-text px-2 py-0.5 rounded text-xs border border-border"
                >
                  {roleNameByCode.get(code) || code}
                </span>
              ))
            ) : (
              <span className="text-text-3">—</span>
            )}
          </div>
        ),
        flex: 2,
        minWidth: 220,
      },
      {
        accessor: "status",
        Header: () => <span>Status</span>,
        Cell: ({ value }) => <StatusPill status={value} />,
        width: 130,
        minWidth: 120,
      },
      {
        accessor: "lastUsedAt",
        Header: () => <span>Last used</span>,
        Cell: ({ value }) =>
          value ? (
            <span className="text-text-2">{fmtDate(value)}</span>
          ) : (
            <span className="text-text-3">Never used</span>
          ),
        width: 180,
        minWidth: 150,
      },
      {
        accessor: "createdAt",
        Header: () => <span>Created</span>,
        Cell: ({ value }) => <span className="text-text-2">{fmtDate(value)}</span>,
        width: 170,
        minWidth: 140,
      },
      ...(canManage
        ? [
            {
              id: "actions",
              align: "right",
              Header: () => null,
              Cell: ({ row }) =>
                row.original.status === "suspended" ? null : (
                  <div className="flex items-center gap-1 flex-wrap justify-end">
                    <button
                      type="button"
                      onClick={() => setRotateCandidate(row.original)}
                      className="px-2.5 py-1 text-xs rounded-ctrl text-primary hover:bg-primary-soft transition-colors border-0 bg-transparent box-border cursor-pointer"
                    >
                      Rotate
                    </button>
                    <button
                      type="button"
                      onClick={() => setRevokeCandidate(row.original)}
                      className="px-2.5 py-1 text-xs rounded-ctrl text-text-2 hover:bg-surface-2 transition-colors border-0 bg-transparent box-border cursor-pointer"
                    >
                      Revoke
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeactivateCandidate(row.original)}
                      className="px-2.5 py-1 text-xs rounded-ctrl text-danger hover:bg-danger-soft transition-colors border-0 bg-transparent box-border cursor-pointer"
                    >
                      Deactivate
                    </button>
                  </div>
                ),
              width: 280,
              minWidth: 240,
            },
          ]
        : []),
    ],
    [canManage, roleNameByCode]
  );

  const isLoading = accountsQ.isLoading || rolesQ.isLoading;
  const error = accountsQ.error;
  const noun = accounts.length === 1 ? "service account" : "service accounts";

  return (
    <Fragment>
      <AdministrationHeader title="Service accounts" showPeriodSelector={false} />
      <div className="w-full p-8">
        <p className="text-sm text-text-2 mb-4 max-w-2xl">
          Service accounts are non-interactive, machine-to-machine principals that authenticate
          with a token and carry a restricted set of capabilities via role assignment. They can
          never approve, delegate, or perform interactive administration — the platform enforces
          this structurally.
        </p>

        <TableCard
          title="Service accounts"
          subtitle={isLoading ? "Loading…" : `Showing ${accounts.length} ${noun}`}
          primaryAction={
            canManage
              ? {
                  label: "New service account",
                  icon: icon({ name: "plus", style: "regular" }),
                  onClick: openCreate,
                }
              : undefined
          }
          isLoading={isLoading}
          error={error}
          errorMessage="Error loading service accounts"
          onRetry={() => accountsQ.refetch()}
        >
          {accounts.length === 0 ? (
            <EmptyState
              icon={icon({ name: "robot", style: "light" })}
              title="No service accounts yet"
              message="Create a service account to let another system authenticate with a restricted set of capabilities."
            />
          ) : (
            <FlexTable columns={columns} data={accounts} getRowId={(a) => a.id} />
          )}
        </TableCard>
      </div>

      {/* Create */}
      <Modal
        show={showCreate}
        close={closeCreate}
        title="New service account"
        description="Give it a label and at least one role. The role's capabilities become the service account's API scope — the platform rejects any role set that would grant an admin/identity capability."
        icon={icon({ name: "robot", style: "solid" })}
        cancelButton="Cancel"
        confirmButton={createMutation.isLoading ? "Creating…" : "Create"}
        confirmAction={() => {
          if (!createMutation.isLoading) handleCreate();
        }}
      >
        <div className="space-y-4" style={{ minWidth: "420px" }}>
          <div>
            <label htmlFor="service-account-label" className="block mb-1 text-sm font-medium text-text">
              Label
            </label>
            <input
              id="service-account-label"
              type="text"
              value={draft.label}
              maxLength={100}
              onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))}
              placeholder="billing-ingest-bot"
              className="border border-solid border-border rounded-ctrl px-3 py-1.5 text-sm w-full box-border focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
          <div>
            <label className="block mb-1 text-sm font-medium text-text">Roles</label>
            <div className="flex flex-wrap gap-2">
              {roles.map((role) => (
                <button
                  key={role.code}
                  type="button"
                  onClick={() => toggleRole(role.code)}
                  className={`px-3 py-1.5 rounded text-sm border border-solid transition-colors cursor-pointer box-border ${
                    draft.roleCodes.includes(role.code)
                      ? "bg-primary text-white border-transparent"
                      : "bg-card text-text-3 border-border hover:border-border-strong hover:bg-surface-2"
                  }`}
                >
                  {role.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </Modal>

      {/* One-time reveal — shared by create + rotate; explicit dismiss only */}
      <Modal
        show={!!revealedToken}
        close={closeReveal}
        title="Copy this token now"
        description="This is the only time the secret is shown. Store it wherever the calling system authenticates from before closing."
        icon={icon({ name: "key", style: "solid" })}
        cancelButton=""
        confirmButton="Done"
        confirmAction={closeReveal}
        size="lg"
      >
        {revealedToken && (
          <div className="flex flex-col gap-2">
            <div className="text-sm font-medium text-text mb-1">Token (secret)</div>
            <CopyField value={revealedToken} />
            <p className="text-xs font-semibold text-danger mt-1">
              Copy this now — you will not be able to see it again.
            </p>
          </div>
        )}
      </Modal>

      {/* Rotate confirm */}
      <Modal
        show={!!rotateCandidate}
        close={() => setRotateCandidate(null)}
        title="Rotate this token?"
        description={
          rotateCandidate
            ? `This immediately invalidates the current token for "${rotateCandidate.label}". Update the calling system with the new token before it needs to authenticate again.`
            : ""
        }
        alertIcon
        icon={icon({ name: "arrows-rotate", style: "solid" })}
        cancelButton="Cancel"
        confirmButton={rotateMutation.isLoading ? "Rotating…" : "Rotate token"}
        confirmAction={() => {
          if (!rotateMutation.isLoading && rotateCandidate) rotateMutation.mutate(rotateCandidate.id);
        }}
      />

      {/* Revoke confirm */}
      <Modal
        show={!!revokeCandidate}
        close={() => setRevokeCandidate(null)}
        title="Revoke this token?"
        description={
          revokeCandidate
            ? `"${revokeCandidate.label}" will no longer be able to authenticate until a new token is rotated in.`
            : ""
        }
        alertIcon
        icon={icon({ name: "ban", style: "solid" })}
        cancelButton="Cancel"
        confirmButton={revokeMutation.isLoading ? "Revoking…" : "Revoke token"}
        confirmAction={() => {
          if (!revokeMutation.isLoading && revokeCandidate) revokeMutation.mutate(revokeCandidate.id);
        }}
      />

      {/* Deactivate confirm */}
      <Modal
        show={!!deactivateCandidate}
        close={() => setDeactivateCandidate(null)}
        title="Deactivate this service account?"
        description={
          deactivateCandidate
            ? `"${deactivateCandidate.label}" will be suspended and can no longer authenticate or call the API.`
            : ""
        }
        alertIcon
        icon={icon({ name: "trash", style: "solid" })}
        cancelButton="Cancel"
        confirmButton={deactivateMutation.isLoading ? "Deactivating…" : "Deactivate account"}
        confirmAction={() => {
          if (!deactivateMutation.isLoading && deactivateCandidate) deactivateMutation.mutate(deactivateCandidate.id);
        }}
      />
    </Fragment>
  );
}
